import { useEffect, useMemo, useState } from 'react';

import { Edit } from '@material-ui/icons';
import { DeleteOutline, Person, PersonAdd } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  MRT_ColumnOrderState,
  MRT_VisibilityState,
} from 'material-react-table';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FullScreenLoader from 'src/components/FullscreenLoading';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  acceptDefaultAlertIndicatorConfigurations,
  deleteWallet,
  getFundIndicators,
  getFundsCompositionByOperationId,
  getIndicatorsColumns,
  getSimpleWalletById,
  getWalletById,
  getWalletFundsWarningSummary,
} from 'src/services/wallet';
import { setBannerMessage } from 'src/store/display';
import { Operation } from 'src/types/operation';
import { CompositionDataItem, IndicatorsResponse } from 'src/types/wallet';

import { DynamicIndicatorsTable } from './components/FundIndicatorsTable';
import { IndicatorDetailsModal } from './components/IndicatorDetailsModal';
import { InfoCards } from './components/InfoCards';
import { ShareWalletModal } from './components/ShareWalletModal';
import { ShowComposition } from './components/ShowComposition';
import { countMonitoredWalletFunds } from './components/wallet-counts';
import { filterIndicatorsByCompetencyDate } from './utils/filterIndicatorsByCompetencyDate';
import { getWalletCompositionData } from './utils/getWalletCompositionData';
import { useMonitorContext } from '../MonitorContextProvider';
import { GuestEnvironmentAlert } from '../components/GuestEnvironmentAlert';
import { GuestEnvironmentSelector } from '../components/Guests/GuestEnvironmentSelector';
import TabNavigation from '../components/TabNavigation';

const EMPTY_WALLET_WARNING_SUMMARY = {
  alertCount: 0,
  criticalCount: 0,
  walletSummaryCount: 0,
};

export default function MonitorWallet() {
  const { indicatorsCompetencyDate } = useMonitorContext();
  const { auth } = useAuthContext();
  const { walletId, ownerId } = useParams<{
    walletId: string | undefined;
    ownerId?: string;
  }>();
  const { wallet, setWallet } = useMonitorContext();
  const [countTotalQuotas, setCountTotalQuotas] = useState(0);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [indicatorName, setIndicatorName] = useState<string>('');
  const [indicatorColumn, setIndicatorColumn] = useState<string>('');
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {},
  );
  const [compositionData, setCompositionData] = useState<CompositionDataItem[]>(
    [],
  );
  const [openWalletCompositionModal, setOpenWalletCompositionModal] =
    useState(false);
  const [openIndicatorDataModal, setOpenIndicatorDataModal] = useState(false);
  const [indicatorData, setIndicatorData] = useState({
    fund: '',
    accessorKeyLabel: '',
    accessorKey: '',
    accessorKeyValue: '',
  });
  const [indicatorsResponseData, setIndicatorsResponseData] = useState<
    IndicatorsResponse[]
  >([]);
  const [walletNumberFormat, setWalletNumberFormat] = useState<
    'percentage' | 'amount'
  >('percentage');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);

  const isOwner = auth.user?.id === wallet?.owner?.id;

  const history = useHistory();
  const dispatch = useDispatch();

  const { setBreadcrumbsItens } = useMonitorContext();

  const handleIndicatorClick = (
    e: React.MouseEvent<HTMLDivElement>,
    fund: string,
    accessorKey: string,
    accessorKeyLabel: string,
    accessorKeyValue: string,
  ) => {
    e.preventDefault();
    setIndicatorData({
      fund,
      accessorKey,
      accessorKeyLabel,
      accessorKeyValue,
    });
    setOpenIndicatorDataModal(true);
  };

  const handleClickIndicatorCell = (
    operation: Operation,
    indicatorName: string,
    indicatorColumn: string,
  ) => {
    setOperation(operation);
    setIndicatorName(indicatorName);
    setIndicatorColumn(indicatorColumn);
    setOpenIndicatorDataModal(true);
  };

  const {
    isLoading: isLoadingWarningSummary,
    data: walletWarningSummary = EMPTY_WALLET_WARNING_SUMMARY,
  } = useQuery({
    queryKey: ['wallet-warning-summary', walletId, indicatorsCompetencyDate],
    queryFn: () =>
      getWalletFundsWarningSummary(walletId, indicatorsCompetencyDate),
    enabled: !!walletId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { isLoading: isLoadingSimpleWallet } = useQuery({
    queryKey: ['simple-wallets', walletId],
    queryFn: () => getSimpleWalletById(walletId || ''),
    onSuccess: (data) => {
      if (!data) {
        history.replace('/monitor');
        return;
      }
      setWallet(data);
    },
    onError: () => {
      history.replace('/monitor');
    },
    enabled: !!walletId,
    refetchOnMount: 'always',
    retry: false,
  });

  const { isLoading: isLoadingWallet, refetch: refetchWallet } = useQuery({
    queryKey: ['wallets', walletId],
    queryFn: () => getWalletById(walletId || ''),
    onSuccess: (data) => {
      setWallet(data);
      const fundsCount = countMonitoredWalletFunds(data?.walletFunds);

      setCountTotalQuotas(fundsCount);
    },
    onError: (error) => {
      history.replace('/monitor');
      toast.error('Erro ao buscar carteira');
    },
    enabled: !!walletId && !isLoadingSimpleWallet,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
  });

  const shouldShowDefaultAlert = useMemo(() => {
    if (!wallet?.walletFunds) return false;

    const hasFundsUsingDefault = wallet.walletFunds.some(
      (fund) => fund.useAlertDefault === true,
    );

    const isConfigNotAccepted = wallet.acceptedDefaultAlertsConfigs === false;

    return hasFundsUsingDefault && isConfigNotAccepted;
  }, [wallet]);

  const { isLoading: isLoadingComposition } = useQuery({
    queryKey: ['wallet-composition', wallet?.id],
    queryFn: () => getFundsCompositionByOperationId(walletId || ''),
    enabled: !!wallet || !isLoadingWallet,
    onSuccess: (data) => {
      setCompositionData(getWalletCompositionData(data));
    },
  });

  const { data: columnsData, isLoading: isLoadingColumns } = useQuery({
    queryKey: ['fund-indicators-columns', walletId],
    queryFn: () => getIndicatorsColumns(Number(walletId)),
    staleTime: 1000 * 60 * 60 * 6, // 6 hours
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingIndicators,
    isFetching: isFetchingIndicatorsData,
  } = useQuery({
    queryKey: ['fund-indicators', walletId, indicatorsCompetencyDate],
    queryFn: () =>
      getFundIndicators(Number(walletId), indicatorsCompetencyDate),
    onSuccess: (data) => {
      const currentFundIndicators = data.map((item: IndicatorsResponse) =>
        filterIndicatorsByCompetencyDate(item, indicatorsCompetencyDate),
      );

      setIndicatorsResponseData(currentFundIndicators);
    },
    enabled: !!walletId && !isLoadingColumns,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { mutate: deleteWalletMutation, isLoading: isLoadingDeleteWallet } =
    useMutation({
      mutationFn: (walletId: number) => deleteWallet(Number(walletId)),
      onSuccess: () => {
        dispatch(
          setBannerMessage({
            message: 'Carteira excluída com sucesso',
            type: 'success',
            autoclose: true,
          }),
        );

        history.replace('/monitor');
      },
      onError: () => {
        dispatch(
          setBannerMessage({
            message: 'Ocorreu um erro ao excluir a carteira',
            type: 'error',
            autoclose: true,
          }),
        );
      },
    });

  const { mutate: mutateAcceptDefaultAlertIndicatorConfigurations } =
    useMutation({
      mutationFn: (walletId: number) =>
        acceptDefaultAlertIndicatorConfigurations(Number(walletId)),
      onSuccess: () => {
        refetchWallet();
      },
      onError: () => {
        history.push('/monitor/alerts');
      },
    });

  const handleDeleteWallet = () => {
    if (walletId) {
      setOpenDeleteDialog(false);
      deleteWalletMutation(Number(walletId));
    }
  };

  const isLoading =
    isLoadingWallet || isLoadingWarningSummary || isLoadingComposition;

  useEffect(() => {
    if (!walletId) {
      history.replace('/monitor');
    }
  }, [walletId, history]);

  // Carteira de fundos seguidos não tem página própria: é exibida na aba
  // "Seguindo" do dashboard do monitor.
  useEffect(() => {
    if (wallet?.purpose === 'followed_funds') {
      history.replace('/monitor?tab=following');
    }
  }, [wallet?.purpose, history]);

  useEffect(() => {
    setWallet(null);
  }, [walletId, setWallet]);

  useEffect(() => {
    if (wallet) {
      const walletLink = ownerId
        ? `/monitor/${ownerId}/wallet/${walletId}`
        : `/monitor/wallet/${walletId}`;

      setBreadcrumbsItens((prev) => [
        ...prev,
        {
          title: `Carteira ${wallet?.name?.toUpperCase()}`,
          link: walletLink,
        },
      ]);

      return () => {
        setBreadcrumbsItens((prev) => prev.slice(0, -1));
      };
    }
  }, [wallet?.name, walletId, history, ownerId]);

  return (
    <>
      {!isOwner && (
        <>
          <GuestEnvironmentSelector />
          <GuestEnvironmentAlert />
        </>
      )}
      <Paper component="section" elevation={2}>
        {shouldShowDefaultAlert && (
          <Alert
            severity="info"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
            action={
              <Box display="flex" gap={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => history.push('/monitor/alerts')}>
                  Editar
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    mutateAcceptDefaultAlertIndicatorConfigurations(
                      Number(walletId),
                    )
                  }>
                  Ok
                </Button>
              </Box>
            }>
            Essa carteira possui fundos que estão utilizando as configurações
            padrões de alerta da sua conta!
          </Alert>
        )}
        <Box
          p={1}
          borderRadius={'5px'}
          display="flex"
          flexDirection="column"
          gap={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={4}>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="h5">
                {wallet?.name?.toUpperCase()}
              </Typography>
              {wallet?.isSharedWallet && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Person sx={{ fontSize: '16px', color: 'text.disabled' }} />
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    fontWeight={600}>
                    {wallet?.owner?.fullName}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" width="30%" justifyContent="flex-end" gap={2}>
              {wallet && wallet.isSharedWallet === false && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={() => setOpenDeleteDialog(true)}>
                  Excluir Carteira
                </Button>
              )}
              {/* TODO: Habilitar futuramente os alertas padrão da carteira
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Notifications />}
                onClick={() =>
                  history.push(`/monitor/wallet/${walletId}/alerts`)
                }>
                Alertas Padrão da Carteira
              </Button> */}
              {wallet && wallet.isSharedWallet === false && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Edit />}
                  onClick={() => {
                    const editLink = ownerId
                      ? `/monitor/${ownerId}/wallet/${walletId}/edit`
                      : `/monitor/wallet/${walletId}/edit`;
                    history.push(editLink);
                  }}>
                  Editar Carteira
                </Button>
              )}
              {wallet && wallet.isSharedWallet === false && (
                <IconButton
                  color="primary"
                  title="Compartilhar Carteira"
                  onClick={() => setOpenShareModal(true)}>
                  <PersonAdd color="inherit" sx={{ fontSize: '28px' }} />
                </IconButton>
              )}
            </Box>
          </Box>
          <InfoCards
            wallet={wallet}
            isLoadingWallet={isLoading}
            handleOpenCompositionModal={() =>
              setOpenWalletCompositionModal(true)
            }
            compositionData={compositionData}
            countTotalQuotas={countTotalQuotas}
            walletWarningSummary={walletWarningSummary}
          />
          <Box display="flex" flexDirection="column" gap={0}>
            <TabNavigation
              value={walletNumberFormat}
              handleSelectTab={setWalletNumberFormat}
            />
            <DynamicIndicatorsTable
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              isLoadingColumns={isLoadingColumns}
              isLoadingIndicators={
                isLoadingIndicators || isFetchingIndicatorsData
              }
              indicatorsResponseData={indicatorsResponseData}
              columnsData={columnsData}
              walletId={parseInt(walletId || '0')}
              valueType={walletNumberFormat}
              handleIndicatorClick={handleClickIndicatorCell}
              ownerId={ownerId}
            />
          </Box>
        </Box>
      </Paper>

      <ShowComposition
        wallet={wallet || undefined}
        compositions={compositionData}
        open={openWalletCompositionModal}
        onClose={() => setOpenWalletCompositionModal(false)}
        onFinish={() => setOpenWalletCompositionModal(false)}
      />
      <IndicatorDetailsModal
        open={openIndicatorDataModal}
        onClose={() => setOpenIndicatorDataModal(false)}
        targetDate={indicatorsCompetencyDate}
        document={operation?.cnpj || ''}
        indicatorName={indicatorName}
        indicatorColumn={indicatorColumn}
      />
      <ShareWalletModal
        walletId={walletId ?? 0}
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        walletName={wallet?.name}
      />

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          Confirmar exclusão da carteira
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir a carteira{' '}
            <strong>{wallet?.name}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenDeleteDialog(false)}
            color="primary"
            autoFocus>
            Cancelar
          </Button>
          <Button onClick={handleDeleteWallet} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <FullScreenLoader
        open={isLoadingDeleteWallet}
        message="Excluindo carteira, aguarde..."
      />
    </>
  );
}
